import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MailCheck } from "lucide-react";

export default function VerifyEmail() {
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const [searchParams] = useSearchParams();
    const emailFromQuery = searchParams.get("email") || "";
    const email = (user && user !== false ? user.email : emailFromQuery) || "";
    const [digits, setDigits] = useState(["", "", "", ""]);
    const [submitting, setSubmitting] = useState(false);
    const [resending, setResending] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    useEffect(() => {
        if (user && user !== false && user.email_verified) {
            navigate("/");
        }
    }, [user, navigate]);

    const handleChange = (idx, val) => {
        const sanitized = val.replace(/\D/g, "").slice(0, 1);
        const next = [...digits];
        next[idx] = sanitized;
        setDigits(next);
        if (sanitized && idx < 3) refs[idx + 1].current?.focus();
    };

    const handlePaste = (e) => {
        const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 4);
        if (text.length === 4) {
            e.preventDefault();
            setDigits(text.split(""));
            refs[3].current?.focus();
        }
    };

    const onKeyDown = (idx, e) => {
        if (e.key === "Backspace" && !digits[idx] && idx > 0) {
            refs[idx - 1].current?.focus();
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const code = digits.join("");
        if (code.length !== 4) {
            toast.error("Ingresa el código completo de 4 dígitos");
            return;
        }
        if (!email) {
            toast.error("Email no disponible");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/verify-email", { email, code });
            toast.success("¡Email verificado! Bienvenido a Play2Win 🎉");
            await refresh?.();
            navigate("/");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Código incorrecto");
        } finally {
            setSubmitting(false);
        }
    };

    const onResend = async () => {
        if (!email || cooldown > 0) return;
        setResending(true);
        try {
            await api.post("/auth/resend-verification", { email });
            toast.success("Código reenviado. Revisa tu correo.");
            setCooldown(60);
        } catch (err) {
            const detail = formatApiErrorDetail(err.response?.data?.detail) || "";
            if (detail.includes("Espera")) {
                const m = detail.match(/(\d+)s/);
                if (m) setCooldown(Number(m[1]));
                toast.error(detail);
            } else {
                toast.error("No se pudo reenviar el código");
            }
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="verify-email-page">
            <div className="w-full max-w-md relative">
                <div className="glow-orb bg-p2w-green w-[300px] h-[300px] -top-20 -right-20 opacity-20" />
                <div className="relative rounded-2xl border border-p2w-line bg-p2w-surface p-8 shadow-glow-violet">
                    <div className="flex items-center gap-2 mb-2 text-p2w-green">
                        <MailCheck className="w-4 h-4" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-bold font-sora">Verifica tu email</span>
                    </div>
                    <h1 className="font-sora font-extrabold text-3xl tracking-tight">Confirma tu cuenta</h1>
                    <p className="mt-1 text-p2w-gray text-sm">
                        Enviamos un código de 4 dígitos a{" "}
                        <span className="text-white font-semibold">{email || "tu correo"}</span>. Es válido 15 minutos.
                    </p>

                    <form onSubmit={onSubmit} className="mt-7 space-y-6">
                        <div className="flex gap-3 justify-center" onPaste={handlePaste}>
                            {digits.map((d, i) => (
                                <input
                                    key={i}
                                    ref={refs[i]}
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    maxLength={1}
                                    value={d}
                                    onChange={(e) => handleChange(i, e.target.value)}
                                    onKeyDown={(e) => onKeyDown(i, e)}
                                    data-testid={`verify-digit-${i}`}
                                    className="w-14 h-16 text-center text-3xl font-sora font-bold rounded-xl bg-p2w-elevated border-2 border-p2w-line text-white focus:outline-none focus:border-p2w-green focus:ring-2 focus:ring-p2w-green/30 transition-all"
                                />
                            ))}
                        </div>
                        <Button
                            type="submit"
                            data-testid="verify-submit"
                            disabled={submitting || digits.join("").length !== 4}
                            className="w-full bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 shadow-glow-green font-semibold py-6 rounded-xl"
                        >
                            {submitting ? "Verificando…" : "Verificar email"}
                        </Button>
                    </form>

                    <div className="mt-6 text-center text-sm text-p2w-gray">
                        ¿No recibiste el código?{" "}
                        <button
                            type="button"
                            onClick={onResend}
                            disabled={resending || cooldown > 0}
                            data-testid="verify-resend"
                            className="text-p2w-green hover:underline font-semibold disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                        >
                            {cooldown > 0 ? `Reenviar (${cooldown}s)` : resending ? "Enviando…" : "Reenviar"}
                        </button>
                    </div>
                    <p className="mt-3 text-xs text-p2w-gray text-center">
                        <Link to="/" className="hover:text-white">Volver al inicio</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
